import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Card — primary surface primitive.
 *
 * Matches the LoginForm / LoginNew aesthetic: `rounded-xl`, `shadow-none`,
 * `border border-border`. Density is a prop so a page can set it per-card:
 *   compact      p=12px   list rows, settings panels
 *   default      p=20px   general use
 *   comfortable  p=32px   marketing hero, empty states
 *
 * CardHeader / CardContent / CardFooter inherit padding from the Card
 * container; they only add vertical rhythm. The previous implementation
 * baked `p-6` into CardHeader/Footer, which stacked padding — avoid that.
 */
const cardVariants = cva(
  "flex flex-col rounded-xl border border-border bg-card text-card-foreground shadow-none",
  {
    variants: {
      density: {
        compact: "p-3 gap-2",
        default: "p-5 gap-4",
        comfortable: "p-8 gap-6",
      },
    },
    defaultVariants: {
      density: "default",
    },
  },
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, density, ...props }, ref) => (
    <div
      ref={ref}
      data-density={density ?? "default"}
      className={cn(cardVariants({ density }), className)}
      {...props}
    />
  ),
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-tight tracking-tight text-foreground",
      className,
    )}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("", className)} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  cardVariants,
};
